import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Paper,
  Text,
  Button,
  TextInput,
  SimpleGrid,
  Group,
  NumberInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import TravelerInfoForm from "../../forms/SingleIssuance/TravelerInfoForm";
import {
  TRAVELER_DOC,
  normalizeName,
  validateName,
  validatePhone,
} from "../../../../utils/issuance";
import nationalities from "../../../../data/nationality.json";
import { mapDataToOptions } from "../../../../utils/mapDataToOptions";
import { USER_TYPES } from "../../../../utils/userTypes";
import ConfirmModal from "../../../../common/components/ConfirmModal/ConfirmModal";
import classes from "./wizard/SingleIssuanceWizard.module.css";

const TravelerInfo = ({
  data,
  coverType,
  handleTraveler,
  operator,
  isSubmitting,
  embedded = false,
  defaultTourOperatorName = "",
}) => {
  const travelerRefs = useRef([]);
  const { user_type } = useSelector((state) => state.auth);
  const operatorProfile = useSelector((state) => state.operatorProfile);
  const [opened, { open, close }] = useDisclosure(false);

  const [allTravelerData, setAllTravelerData] = useState([]);
  const [tourOperatorName, setTourOperatorName] = useState(
    user_type[0] === USER_TYPES.OPERATOR
      ? operatorProfile.operator_name
      : defaultTourOperatorName
  );

  useEffect(() => {
    if (defaultTourOperatorName) {
      setTourOperatorName(defaultTourOperatorName);
    }
  }, [defaultTourOperatorName]);

  const [operatorPhoneNumber, setOperatorPhoneNumber] = useState(
    user_type[0] === USER_TYPES.OPERATOR
      ? operatorProfile.operator.phone_number
      : ""
  );
  const [tripErrors, setTripErrors] = useState({ name: "", phone: "" });
  const [tripTouched, setTripTouched] = useState({ name: false, phone: false });

  const updateTripNameError = (value) => {
    const result = validateName(value, { label: "Tour operator name" });
    setTripErrors((prev) => ({
      ...prev,
      name: result === true ? "" : result,
    }));
  };

  const updateTripPhoneError = (value) => {
    const result = validatePhone(value, { label: "Operator phone number" });
    setTripErrors((prev) => ({
      ...prev,
      phone: result === true ? "" : result,
    }));
  };

  const requireDocument = (
    nationality_id,
    traveller_from,
    traveller_to,
    residence_status = "no"
  ) => {
    const requiredDocuments = [];

    const getCountryName = (id) =>
      nationalities.find((country) => country.id === +id)?.name || null;

    const nationality = getCountryName(nationality_id);

    // Residence Proof
    if (
      nationality !== "Indian" &&
      residence_status &&
      residence_status === "yes"
    ) {
      requiredDocuments.push(TRAVELER_DOC.RESIDENCE_PROOF);
    }

    // Passport Front and Back
    if (
      nationality !== "Indian" ||
      traveller_from !== "India" ||
      traveller_to !== "India"
    ) {
      requiredDocuments.push(TRAVELER_DOC.PASSPORT_FRONT);
      requiredDocuments.push(TRAVELER_DOC.PASSPORT_BACK);
    }

    // KYC Document (Aadhar Card) and Flight Ticket
    if (
      (nationality === "Indian" &&
        traveller_from === "India" &&
        traveller_to !== "India") ||
      (nationality !== "Indian" &&
        residence_status &&
        residence_status === "yes" &&
        traveller_from === "India" &&
        traveller_to !== "India")
    ) {
      requiredDocuments.push(TRAVELER_DOC.AADHAR_FRONT);
      requiredDocuments.push(TRAVELER_DOC.AADHAR_BACK);
      requiredDocuments.push(TRAVELER_DOC.FLIGHT_TICKET);
    }

    return requiredDocuments;
  };

  const handleIndividualSubmit = (travelerData, index) => {
    const coverPricingId = data.covers[coverType][index];
    setAllTravelerData((prevData) => {
      const newData = [...prevData];
      newData[index] = { ...travelerData, cover_pricing_id: coverPricingId };
      return newData;
    });
  };

  const validateTripDetails = () => {
    if (operator) return true;
    setTripTouched({ name: true, phone: true });
    const nameResult = validateName(tourOperatorName, {
      label: "Tour operator name",
    });
    const phoneResult = validatePhone(operatorPhoneNumber, {
      label: "Operator phone number",
    });
    setTripErrors({
      name: nameResult === true ? "" : nameResult,
      phone: phoneResult === true ? "" : phoneResult,
    });
    return nameResult === true && phoneResult === true;
  };

  const handleSubmit = async () => {
    const tripValid = validateTripDetails();
    const results = await Promise.all(
      travelerRefs.current.filter(Boolean).map((formRef) => formRef.submit())
    );
    if (tripValid && results.every(Boolean)) open();
  };

  const handleConfirmSubmit = () => {
    if (
      allTravelerData.length === parseInt(data.quote_data.age_fields.length)
    ) {
      handleTraveler(
        allTravelerData,
        operatorProfile.operator.id,
        tourOperatorName,
        operatorPhoneNumber
      );
    }
    close();
  };

  const sectionPaperProps = embedded ? { p: 0, shadow: undefined, withBorder: false } : { p: "lg", my: "lg" };
  const sectionTitleMb = embedded ? "sm" : "xl";
  const formSpacing = embedded ? "md" : "lg";

  return (
    <>
      {!operator && (
        <Paper {...sectionPaperProps}>
          <Text fw={600} mb={sectionTitleMb}>
            Trip Details
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={formSpacing}>
            <TextInput
              label="Tour Operator Name"
              value={tourOperatorName}
              onChange={(e) => {
                const value = e.target.value;
                setTourOperatorName(value);
                setTripTouched((prev) => ({ ...prev, name: true }));
                updateTripNameError(value);
              }}
              onBlur={(e) => {
                const value = normalizeName(e.target.value);
                setTourOperatorName(value);
                setTripTouched((prev) => ({ ...prev, name: true }));
                updateTripNameError(value);
              }}
              placeholder="Tour Operator Name"
              error={
                tripTouched.name && tripErrors.name ? tripErrors.name : undefined
              }
              withAsterisk
            />
            <NumberInput
              label="Operator Phone Number"
              value={operatorPhoneNumber}
              onChange={(value) => {
                setOperatorPhoneNumber(value);
                setTripTouched((prev) => ({ ...prev, phone: true }));
                updateTripPhoneError(value);
              }}
              placeholder="Operator Phone Number"
              hideControls
              allowDecimal={false}
              allowNegative={false}
              clampBehavior="none"
              error={
                tripTouched.phone && tripErrors.phone ? tripErrors.phone : undefined
              }
              withAsterisk
            />
          </SimpleGrid>
        </Paper>
      )}

      {[...Array(parseInt(data.quote_data.age_fields.length))].map(
        (_, index) => (
          <Paper
            {...sectionPaperProps}
            key={index}
            mt={embedded ? (index === 0 ? "lg" : "md") : undefined}
          >
            <Text fw={600} mb={sectionTitleMb}>
              Traveler {index + 1}
            </Text>

            <TravelerInfoForm
              compact={embedded}
              ref={(el) => (travelerRefs.current[index] = el)}
              travellerIndex={index}
              formData={data}
              options={mapDataToOptions(nationalities)}
              getRequiredDocs={requireDocument}
              onSubmit={handleIndividualSubmit}
            />
          </Paper>
        )
      )}

      <Group
        justify="center"
        mt="xl"
        className={embedded ? classes.submitRow : undefined}
      >
        <Button
          color="brand"
          onClick={handleSubmit}
          loading={isSubmitting}
          loaderProps={{ type: "dots" }}
        >
          Submit
        </Button>
      </Group>

      <ConfirmModal
        opened={opened}
        onClose={close}
        onConfirm={handleConfirmSubmit}
        title="Policy issuance confirmation"
        confirmLabel="Confirm"
        cancelLabel="Cancel"
      >
        Are you sure you want issue policy ! Verify All details
      </ConfirmModal>
    </>
  );
};

export default TravelerInfo;
